import { Link } from 'react-router-dom';
import { cn } from './cn';
import Reveal from './Reveal';

type CardBase = { to: string; image?: string; title: string; eyebrow?: string; text?: string; className?: string; delay?: number };

/** Carte à image 3:2 avec zoom doux au survol: projets, articles, marchés. */
export function MediaCard({ to, image, title, eyebrow, text, className, delay }: CardBase) {
  return (
    <Reveal delay={delay} className={className}>
      <Link to={to} className="group block">
        <div className="relative aspect-[3/2] overflow-hidden bg-secondary-200">
          {image && (
            <img src={image} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
          )}
          <span aria-hidden className="absolute inset-x-0 bottom-0 h-1 origin-left scale-x-0 bg-brand transition-transform duration-500 ease-out group-hover:scale-x-100" />
        </div>
        <div className="pt-5">
          {eyebrow && <div className="mb-2 text-xs font-semibold uppercase tracking-[0.0625rem] text-ink">{eyebrow}</div>}
          <h3 className="text-inherit font-display text-2xl font-bold normal-case leading-tight tracking-normal transition-colors group-hover:text-brand-dark">{title}</h3>
          {text && <p className="mt-3 max-h-[4.875rem] overflow-hidden text-[1rem] leading-relaxed text-ink">{text}</p>}
        </div>
      </Link>
    </Reveal>
  );
}

/** Carte pleine image avec voile sombre et titre en bas: secteurs et services. */
export function ImageTile({ to, image, title, text, className, delay }: CardBase) {
  return (
    <Reveal delay={delay} className={className}>
      <Link to={to} className="group relative block aspect-[4/5] overflow-hidden bg-charcoal text-white">
        {image && <img src={image} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-80 transition-all duration-700 group-hover:scale-105 group-hover:opacity-60" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-6">
          <h3 className="text-inherit font-display text-2xl font-bold normal-case leading-tight tracking-normal">{title}</h3>
          {text && <p className="mt-2 max-h-0 overflow-hidden text-sm opacity-0 transition-all duration-500 group-hover:max-h-12 group-hover:opacity-100">{text}</p>}
          <span aria-hidden className="mt-4 block h-0.5 w-10 bg-brand transition-all duration-300 group-hover:w-20" />
        </div>
      </Link>
    </Reveal>
  );
}

type ContactProps = { name: string; role: string; image?: string; email?: string; phone?: string; className?: string };

export function ContactCard({ name, role, image, email, phone, className }: ContactProps) {
  return (
    <div className={cn('flex items-center gap-5 bg-surface p-6', className)}>
      {image && <img src={image} alt={name} loading="lazy" className="h-20 w-20 shrink-0 rounded-full object-cover" />}
      <div className="min-w-0">
        <div className="text-inherit font-display text-xl font-bold">{name}</div>
        <div className="text-sm text-ink">{role}</div>
        {email && <a href={`mailto:${email}`} className="mt-1 block truncate text-sm font-medium text-charcoal underline decoration-brand underline-offset-4 hover:text-brand-dark">{email}</a>}
        {phone && <a href={`tel:${phone}`} className="block text-sm text-ink">{phone}</a>}
      </div>
    </div>
  );
}
